import axios, { AxiosInstance } from 'axios';

import { Configuration } from 'src/Services/API/generated';
import {
  BedDto,
  BuildingDto,
  CartographySummaryDto,
  CartographyTreeDto,
  CreateBedDto,
  CreateBuildingDto,
  CreateFloorDto,
  CreateRoomDto,
  FloorDto,
  RoomDto,
  UpdateBedDto,
  UpdateBuildingDto,
  UpdateFloorDto,
  UpdateRoomDto,
} from 'src/types/cartography.types';

export class CartographyApi {
  private axiosInstance: AxiosInstance;

  constructor(configuration: Configuration) {
    this.axiosInstance = axios.create({
      baseURL: configuration.basePath ?? '',
      ...configuration.baseOptions,
    });
  }

  private base(assetId: string): string {
    return `/assets/${assetId}/cartography`;
  }

  async getTree(assetId: string): Promise<CartographyTreeDto> {
    const { data } = await this.axiosInstance.get<CartographyTreeDto>(this.base(assetId));
    return data;
  }

  async getSummary(assetId: string): Promise<CartographySummaryDto> {
    const { data } = await this.axiosInstance.get<CartographySummaryDto>(
      `${this.base(assetId)}/summary`
    );
    return data;
  }

  // Buildings
  async createBuilding(assetId: string, payload: CreateBuildingDto): Promise<BuildingDto> {
    const { data } = await this.axiosInstance.post<BuildingDto>(
      `${this.base(assetId)}/buildings`,
      payload
    );
    return data;
  }

  async updateBuilding(
    assetId: string,
    buildingId: string,
    payload: UpdateBuildingDto
  ): Promise<BuildingDto> {
    const { data } = await this.axiosInstance.put<BuildingDto>(
      `${this.base(assetId)}/buildings/${buildingId}`,
      payload
    );
    return data;
  }

  async deleteBuilding(assetId: string, buildingId: string): Promise<void> {
    await this.axiosInstance.delete(`${this.base(assetId)}/buildings/${buildingId}`);
  }

  // Floors
  async createFloor(
    assetId: string,
    buildingId: string,
    payload: CreateFloorDto
  ): Promise<FloorDto> {
    const { data } = await this.axiosInstance.post<FloorDto>(
      `${this.base(assetId)}/buildings/${buildingId}/floors`,
      payload
    );
    return data;
  }

  async updateFloor(assetId: string, floorId: string, payload: UpdateFloorDto): Promise<FloorDto> {
    const { data } = await this.axiosInstance.put<FloorDto>(
      `${this.base(assetId)}/floors/${floorId}`,
      payload
    );
    return data;
  }

  async deleteFloor(assetId: string, floorId: string): Promise<void> {
    await this.axiosInstance.delete(`${this.base(assetId)}/floors/${floorId}`);
  }

  // Rooms
  async createRoom(assetId: string, payload: CreateRoomDto): Promise<RoomDto> {
    const { data } = await this.axiosInstance.post<RoomDto>(`${this.base(assetId)}/rooms`, payload);
    return data;
  }

  async updateRoom(assetId: string, roomId: string, payload: UpdateRoomDto): Promise<RoomDto> {
    const { data } = await this.axiosInstance.put<RoomDto>(
      `${this.base(assetId)}/rooms/${roomId}`,
      payload
    );
    return data;
  }

  async deleteRoom(assetId: string, roomId: string): Promise<void> {
    await this.axiosInstance.delete(`${this.base(assetId)}/rooms/${roomId}`);
  }

  // Beds
  async createBed(assetId: string, roomId: string, payload: CreateBedDto): Promise<BedDto> {
    const { data } = await this.axiosInstance.post<BedDto>(
      `${this.base(assetId)}/rooms/${roomId}/beds`,
      payload
    );
    return data;
  }

  async updateBed(assetId: string, bedId: string, payload: UpdateBedDto): Promise<BedDto> {
    const { data } = await this.axiosInstance.put<BedDto>(
      `${this.base(assetId)}/beds/${bedId}`,
      payload
    );
    return data;
  }

  async deleteBed(assetId: string, bedId: string): Promise<void> {
    await this.axiosInstance.delete(`${this.base(assetId)}/beds/${bedId}`);
  }
}
